import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useWebhookActions } from "@/features/webhooks/hooks/useWebhookActions";
import { useRequiredOrgId } from "@/hooks/useRequiredOrgId";

export const DeleteWebhookDialog = ({
  webhookId,
  onClose,
}: {
  webhookId: string | null;
  onClose: () => void;
}) => {
  const organizationId = useRequiredOrgId();
  const { deleteWebhook } = useWebhookActions();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(!!webhookId);
  }, [webhookId]);

  const handleDelete = () => {
    if (!webhookId) return;
    deleteWebhook.mutate(
      { organizationId, webhookId },
      {
        onSuccess: () => {
          onClose();
          toast.success("Webhook deleted", {
            description: "Webhook deleted successfully",
          });
        },
        onError: (error) => {
          toast.error("Error deleting webhook", {
            description: error.message,
          });
        },
      },
    );
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Webhook</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently remove the webhook and stop all notifications
            to this endpoint.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <p className="text-sm text-muted-foreground">
          Are you sure you want to delete this webhook? This action is
          irreversible.
        </p>
        <AlertDialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteWebhook.isPending}
          >
            Confirm
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
